import React from "react";
import { useServices } from "../../services/Services";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import css from "./Sidebar.module.css";
import { CircularProgress } from "@mui/material";
import { useSubscriptionWithQuery } from "../../hooks/useSubscription";

interface AnalysisData {
  id: string;
  title: string | undefined;
  createdAt: string | undefined;
}

interface InfiniteQueryPage {
  data: AnalysisData[];
  nextPageToken?: string;
}

interface SidebarEventSectionProps {
  userId: string;
  isCurrentRoute: (path: string) => boolean;
  onItemSelect: () => void;
}

const SidebarEventSection: React.FC<SidebarEventSectionProps> = ({
  userId,
  isCurrentRoute,
  onItemSelect,
}) => {
  const services = useServices();
  const createEventDeepDiveRequest =
    services.Request.useCreateEventDeepDiveRequest();

  // Subscribe to event deep dive requests and read from cached data
  const { data: trackedEventDeepDiveRequests = [] } = useSubscriptionWithQuery({
    subscribe: () =>
      services.Request.subscribeToEventDeepDiveRequest(userId, () => {}),
    query: services.Request.useGetTrackedEventDeepDiveRequests(userId),
    enabled: !!userId,
    onError: (error) => {
      console.error("Failed to subscribe to event deep dive requests:", error);
    },
  });

  const {
    data: eventData,
    fetchNextPage: fetchNextEventPage,
    hasNextPage: hasNextEventPage,
    isFetchingNextPage: isFetchingNextEventPage,
  } = services.Analysis.useGetEventDeepDivesInfinite(userId, 10);

  const eventDeepDives: AnalysisData[] =
    eventData?.pages?.flatMap((page: InfiniteQueryPage) => page.data) ?? [];

  // Extract unique eventIds from tracked requests
  const eventIds = [
    ...new Set(
      trackedEventDeepDiveRequests
        .map((r) => r.eventId)
        .filter((id): id is string => !!id),
    ),
  ];

  // Fetch event names for all eventIds
  const { data: events = [] } =
    services.Event.useGetSourceEventsByIds(eventIds);

  // Create a lookup map: eventId -> eventName
  const eventNameMap = new Map(events.map((e) => [e.id, e.name]));

  const newEventRequests = trackedEventDeepDiveRequests.filter(
    (r) => r.status === "New",
  );
  const pendingEventRequests = trackedEventDeepDiveRequests.filter(
    (r) => r.status === "Pending",
  );
  const failedEventRequests = trackedEventDeepDiveRequests.filter(
    (r) => r.status === "Failed",
  );

  const handleRetry = async (eventId: string) => {
    try {
      await createEventDeepDiveRequest.mutateAsync({
        eventId,
        userId,
      });
    } catch (error) {
      console.error("Failed to retry event deep dive:", error);
    }
  };

  return (
    <>
      <div className={css.sectionTitle}>
        <span className={css.sectionTitleText}>Event Deep-Dives</span>
      </div>
      <div className={css.insightsList}>
        {/* New, Pending and Failed Requests */}
        {(newEventRequests.length > 0 ||
          pendingEventRequests.length > 0 ||
          failedEventRequests.length > 0) && (
          <>
            {newEventRequests.map((eventRequest) => {
              const eventName =
                eventNameMap.get(eventRequest.eventId || "") ||
                eventRequest.eventId ||
                "New Event Analysis";
              return (
                <div
                  key={`${eventRequest.id}-${eventRequest.eventId}`}
                  className={`${css.insightItem} ${css.newItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={eventName}>
                      {eventName}
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingEventRequests.map((eventRequest) => {
              const eventName =
                eventNameMap.get(eventRequest.eventId || "") ||
                eventRequest.eventId ||
                "Processing Event Analysis";
              return (
                <div
                  key={`${eventRequest.id}-${eventRequest.eventId}`}
                  className={`${css.insightItem} ${css.pendingItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={eventName}>
                      {eventName}
                    </div>
                  </div>
                  <div className={css.loadingSpinner}>
                    <CircularProgress size={16} />
                  </div>
                </div>
              );
            })}
            <div className={css.sectionSeparator} />
            {failedEventRequests.map((eventRequest) => {
              const eventName =
                eventNameMap.get(eventRequest.eventId || "") ||
                eventRequest.eventId ||
                "Failed Event Analysis";
              return (
                <div
                  key={`${eventRequest.id}-${eventRequest.eventId}`}
                  className={`${css.insightItem} ${css.failedItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={eventName}>
                      {eventName}
                    </div>
                  </div>
                  <button
                    className={css.retryButton}
                    onClick={() => handleRetry(eventRequest.eventId || "")}
                    disabled={createEventDeepDiveRequest.isPending}
                  >
                    Retry
                  </button>
                </div>
              );
            })}
          </>
        )}

        {/* Completed Deep Dives */}
        {eventDeepDives.map((analysis) => {
          const path = `/event-analysis/${analysis.id}`;
          const title = analysis.title || "Event Analysis";
          return (
            <Link
              key={analysis.id}
              to={path}
              onClick={onItemSelect}
              className={`${css.insightItem} ${isCurrentRoute(path) ? css.currentRoute : ""}`}
            >
              <div className={css.itemContent}>
                <div className={css.itemTitle} title={title}>
                  {title}
                </div>
              </div>
              <div className={css.itemDate}>
                {analysis.createdAt
                  ? format(new Date(analysis.createdAt), "MMM dd")
                  : "Recent"}
              </div>
            </Link>
          );
        })}
        {hasNextEventPage && (
          <button
            className={css.loadMoreButton}
            onClick={() => fetchNextEventPage()}
            disabled={isFetchingNextEventPage}
          >
            {isFetchingNextEventPage ? "Loading..." : "Load More"}
          </button>
        )}
      </div>
    </>
  );
};

export default SidebarEventSection;
